import { isBefore } from 'date-fns';
import * as Yup from 'yup';

const weekDays = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

export default weekDays;

export const workingHoursTemplate = {
  dayOfWeek: '',
  startTime: null,
  endTime: null,

  startLunchTime: null,
  endLunchTime: null,
};

export const currentDayISODate = new Date().toISOString().slice(0, 11);

export const weekDaysEnum = {
  MONDAY: { key: 'MONDAY', value: 1, name: 'Monday', shortName: 'Mon' },
  TUESDAY: { key: 'TUESDAY', value: 2, name: 'Tuesday', shortName: 'Tue' },
  WEDNESDAY: {
    key: 'WEDNESDAY',
    value: 3,
    name: 'Wednesday',
    shortName: 'Wed',
  },
  THURSDAY: { key: 'THURSDAY', value: 4, name: 'Thursday', shortName: 'Thu' },
  FRIDAY: { key: 'FRIDAY', value: 5, name: 'Friday', shortName: 'Fri' },
  SATURDAY: { key: 'SATURDAY', value: 6, name: 'Saturday', shortName: 'Sat' },
  SUNDAY: { key: 'SUNDAY', value: 7, name: 'Sunday', shortName: 'Sun' },
};

export const openHoursSchema = Yup.object().shape({
  morningOpeningTime: Yup.date().when('morningClosingTime', {
    is: (val) => val,
    then: Yup.date()
      .max(
        Yup.ref('morningClosingTime'),
        'The end time cannot be earlier than the start time'
      )
      .nullable(),
    otherwise: Yup.date({ accept: [`Invalid Date`] }).nullable(),
  }),
  morningClosingTime: Yup.date({ accept: [`Invalid Date`] })
    .nullable()
    .when('afternoonOpeningTime', {
      is: (val) => val,
      then: Yup.date()
        .max(
          Yup.ref('afternoonOpeningTime'),
          'Morning end time cannot be later than the afternoon start time'
        )
        .nullable(),
      otherwise: Yup.date({ accept: [`Invalid Date`] }).nullable(),
    }),
  afternoonOpeningTime: Yup.date().when('afternoonClosingTime', {
    is: (val) => val,
    then: Yup.date()
      .max(
        Yup.ref('afternoonClosingTime'),
        'The end time cannot be earlier than the start time'
      )
      .nullable(),
    otherwise: Yup.date({ accept: [`Invalid Date`] }).nullable(),
  }),
  afternoonClosingTime: Yup.date().nullable(),
});

export const availabilitySchema = Yup.object().shape({
  morningOpeningTime: Yup.date().when('morningClosingTime', {
    is: (val) => val,
    then: Yup.date()
      .max(
        Yup.ref('morningClosingTime'),
        'Start lunch time should be later than start work time'
      )
      .nullable(),
    otherwise: Yup.date({ accept: [`Invalid Date`] }).nullable(),
  }),
  morningClosingTime: Yup.date({ accept: [`Invalid Date`] })
    .nullable()
    .when('afternoonOpeningTime', {
      is: (val) => val,
      then: Yup.date()
        .max(
          Yup.ref('afternoonOpeningTime'),
          'Start of lunch should be earlier than end of lunch'
        )
        .nullable(),
      otherwise: Yup.date({ accept: [`Invalid Date`] }).nullable(),
    }),
  afternoonOpeningTime: Yup.date().when('afternoonClosingTime', {
    is: (val) => val,
    then: Yup.date()
      .max(
        Yup.ref('afternoonClosingTime'),
        "End lunch time should be earlier than end work time"
      )
      .nullable(),
    otherwise: Yup.date({ accept: [`Invalid Date`] }).nullable(),
  }),
  afternoonClosingTime: Yup.date().nullable(),
});

export const daysOfWeek = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

export const daysOfWeekSchema = Yup.object()
  .shape(
    daysOfWeek.reduce((schema, day) => {
      schema[day] = openHoursSchema;
      return schema;
    }, {})
  )
  .test('valid-times', 'Invalid times', function (value) {
    for (let day in value) {
      const {
        morningOpeningTime,
        morningClosingTime,
        afternoonOpeningTime,
        afternoonClosingTime,
      } = value[day];

      // Check if either all fields are filled or only morningOpeningTime and afternoonClosingTime are present
      const allFieldsFilled =
        morningOpeningTime &&
        morningClosingTime &&
        afternoonOpeningTime &&
        afternoonClosingTime;
      const onlyMorningAndAfternoon =
        morningOpeningTime &&
        afternoonClosingTime &&
        !morningClosingTime &&
        !afternoonOpeningTime;

      const noFieldsFilled =
        !morningOpeningTime &&
        !morningClosingTime &&
        !afternoonOpeningTime &&
        !afternoonClosingTime;

      if (
        !isBefore(morningOpeningTime, afternoonClosingTime) &&
        !noFieldsFilled
      ) {
        if (!morningOpeningTime) {
          return this.createError({
            path: `${day}.morningOpeningTime`,
            message: 'Please fill opening time',
          });
        }
        if (!afternoonClosingTime) {
          return this.createError({
            path: `${day}.afternoonClosingTime`,
            message: 'Please fill closing time',
          });
        }
        return this.createError({
          path: `${day}.morningOpeningTime`,
          message: 'Opening time should be earlier than closing time',
        });
      }

      if (!allFieldsFilled && !onlyMorningAndAfternoon && !noFieldsFilled) {
        return this.createError({
          path: `${day}.morningOpeningTime`,
          message:
            'Either fill in all fields or only morning opening time and afternoon closing time',
        });
      }
    }

    return true;
  });

export const availabilityWeekSchema = Yup.object()
  .shape(
    daysOfWeek.reduce((schema, day) => {
      schema[day] = availabilitySchema;
      return schema;
    }, {})
  )
  .test('valid-times', 'Invalid times', function (value) {
    for (let day in value) {
      const {
        morningOpeningTime,
        morningClosingTime,
        afternoonOpeningTime,
        afternoonClosingTime,
      } = value[day];

      // Check if either all fields are filled or only morningOpeningTime and afternoonClosingTime are present
      const allFieldsFilled =
        morningOpeningTime &&
        morningClosingTime &&
        afternoonOpeningTime &&
        afternoonClosingTime;
      const onlyMorningAndAfternoon =
        morningOpeningTime &&
        afternoonClosingTime &&
        !morningClosingTime &&
        !afternoonOpeningTime;

      const noFieldsFilled =
        !morningOpeningTime &&
        !morningClosingTime &&
        !afternoonOpeningTime &&
        !afternoonClosingTime;

      if (
        !isBefore(morningOpeningTime, afternoonClosingTime) &&
        !noFieldsFilled
      ) {
        if (!morningOpeningTime) {
          return this.createError({
            path: `${day}.morningOpeningTime`,
            message: 'Please fill opening time',
          });
        }
        if (!afternoonClosingTime) {
          return this.createError({
            path: `${day}.afternoonClosingTime`,
            message: 'Please fill closing time',
          });
        }
        return this.createError({
          path: `${day}.morningOpeningTime`,
          message: 'Opening time should be earlier than closing time',
        });
      }

      if (!allFieldsFilled && !onlyMorningAndAfternoon && !noFieldsFilled) {
        return this.createError({
          path: `${day}.morningOpeningTime`,
          message:
            'Either fill in all fields or only morning opening time and afternoon closing time',
        });
      }
    }

    return true;
  });
